declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[][];
      push?: unknown;
      loaded?: boolean;
      version?: string;
    };
    _fbq?: unknown;
  }
}

const FBEventsSrc = "https://connect.facebook.net/en_US/fbevents.js";
const initializedPixelIds = new Set<string>();

/**
 * Meta Pixel base code — matches the official snippet:
 * https://developers.facebook.com/docs/meta-pixel/get-started
 */
function ensureFbqStubAndScript(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  !(function (
    f: Window,
    b: Document,
    e: string,
    v: string,
    n?: unknown,
    t?: HTMLScriptElement,
    s?: Element | null
  ) {
    if (f.fbq) return;
    n = f.fbq = function (...args: unknown[]) {
      const fb = f.fbq!;
      if (fb.callMethod) {
        fb.callMethod.apply(fb, args);
      } else {
        fb.queue.push(args);
      }
    } as typeof f.fbq;
    if (!f._fbq) f._fbq = n;
    (f.fbq as { push: typeof f.fbq }).push = f.fbq;
    f.fbq!.loaded = true;
    f.fbq!.version = "2.0";
    f.fbq!.queue = [];
    t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s?.parentNode?.insertBefore(t, s);
  })(window, document, "script", FBEventsSrc);
}

export function metaPixelNoScriptPageViewSrc({ pixelId }: { pixelId: string }): string {
  const id = pixelId.trim();
  return `https://www.facebook.com/tr?id=${encodeURIComponent(id)}&ev=PageView&noscript=1`;
}

/** Loads the Meta Pixel script (once), then `fbq('init', pixelId)` and `fbq('track', 'PageView')` per official setup. */
export function initMetaPixelCheckout({ pixelId }: { pixelId: string }): void {
  const id = pixelId.trim();
  if (!id || typeof window === "undefined") return;

  ensureFbqStubAndScript();

  if (!initializedPixelIds.has(id)) {
    window.fbq?.("init", id);
    initializedPixelIds.add(id);
  }
  window.fbq?.("track", "PageView");
}

export function trackMetaPixel({
  event,
  payload,
}: {
  event: string;
  payload?: Record<string, unknown>;
}): void {
  if (typeof window === "undefined" || !window.fbq) return;
  if (payload != null && Object.keys(payload).length > 0) {
    window.fbq("track", event, payload);
  } else {
    window.fbq("track", event);
  }
}

export function metaPixelContentsFromCart({
  items,
}: {
  items: Array<{ productId: string; quantity: number; unitPriceCents: number }>;
}): {
  value: number;
  currency: string;
  content_ids: string[];
  contents: Array<{ id: string; quantity: number; item_price: number }>;
  num_items: number;
} {
  const contents = items.map((item) => ({
    id: item.productId,
    quantity: item.quantity,
    item_price: Math.round(item.unitPriceCents) / 100,
  }));
  const valueCents = items.reduce(
    (sum, item) => sum + Math.round(item.unitPriceCents) * item.quantity,
    0
  );
  return {
    value: Math.round(valueCents) / 100,
    currency: "USD",
    content_ids: items.map((i) => i.productId),
    contents,
    num_items: items.reduce((s, i) => s + i.quantity, 0),
  };
}

/** Uses `valueCents` as the event value (e.g. PWYW total, shipping, tips) while contents reflect line items. */
export function metaPixelCheckoutPayload({
  valueCents,
  items,
}: {
  valueCents: number;
  items: Array<{ productId: string; quantity: number; unitPriceCents: number }>;
}): {
  value: number;
  currency: string;
  content_type: string;
  content_ids: string[];
  contents: Array<{ id: string; quantity: number; item_price: number }>;
  num_items: number;
} {
  const { currency, content_ids, contents, num_items } = metaPixelContentsFromCart({
    items,
  });
  return {
    value: Math.round(valueCents) / 100,
    currency,
    content_type: "product",
    content_ids,
    contents,
    num_items,
  };
}
